
import React from 'react'
import { Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notification } from '../../../../components/Notifications'
import { RoleManager } from '../../../../APIManager'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { grey } from '../../../../colors'
import { useUserProfile } from '../../../../hooks/useUserProfile'

interface DeleteUserRoleProps {
  selectedRow?: any
  roleId: string
  roleName: string
  handleCloseModal?: () => void
  showErrorModal?: () => void
  setErrorMessage?: (arg: string) => void
}

const DeleteUserRole: React.FC<DeleteUserRoleProps> = ({
  selectedRow,
  roleId,
  roleName,
  handleCloseModal,
  showErrorModal,
  setErrorMessage
}) => {
  const theme = useTheme()
  const queryClient = useQueryClient()

  const { data: userProfile } = useUserProfile()
  const currentLoggedInUserId = userProfile?.user?.userId

  const deleteUserRoleMutation = useMutation({
    mutationFn: async ({ userId, roleId }: { userId: string, roleId: string }) => {
      return await RoleManager.deleteUserRole(userId, roleId)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roleData'] })
      void queryClient.invalidateQueries({ queryKey: ['rolesSummary'] })
      notification('Role removed successfully', { variant: 'success' })
      if (selectedRow?.userId === currentLoggedInUserId) {
        localStorage.removeItem('currentRoleId')
        window.location.reload()
      }
      if (handleCloseModal) {
        handleCloseModal()
      }
    }
  })

  const handleDelete = async (): Promise<void> => {
    if (!selectedRow?.userId) {
      notification('No user ID found in selected row', { variant: 'error' })
      return
    }
    try {
      await deleteUserRoleMutation.mutateAsync({
        userId: selectedRow?.userId,
        roleId
      })
    } catch (error: any) {
      // Check for 422 Unprocessable Entity
      if (error?.status === 422) {
        const backendMessage = error?.message?.message?.[0] || 'Unable to remove user from role.'
        if (setErrorMessage && showErrorModal) {
          setErrorMessage(backendMessage)
          showErrorModal()
        } else {
          notification(backendMessage, { variant: 'error' })
        }
      } else {
        notification('An unexpected error occurred while removing the user.', { variant: 'error' })
      }
    }
  }

  const userName = selectedRow?.name || selectedRow?.email || 'this user'
  const userEmail = selectedRow?.email ? ` (${selectedRow.email})` : ''
  const isDeleting = deleteUserRoleMutation.isPending

  return (
    <>
      <Typography fontSize='14px' lineHeight={theme.spacing(5)} color={grey[850]}>
        Are you sure you want to remove <strong>{userName}{userEmail}</strong> from the <strong>{roleName}</strong> role?
      </Typography>
      <DefaultModalActions
        confirmButtonProps={{
          onClick: handleDelete,
          text: isDeleting ? 'Removing...' : 'Remove User',
          loading: isDeleting,
          buttonType: ButtonType.Destructive
        }}
        destructiveButtonProps={{
          onClick: handleCloseModal
        }}
      />
    </>
  )
}

export default DeleteUserRole
